/**
 * i18n namespace for the auth feature — FR default + AR (RTL-aware). Registered once in the
 * module's onRegister hook. Keys are dot-namespaced under `auth.*` so no central dictionary owns
 * them. Mono numerics (token expiry, ids) keep LTR via `nv-mono nv-ltr-island` in the views.
 */
import { registerMessages } from "@/app";

export function registerAuthMessages(): void {
  registerMessages("fr", {
    "nav.auth.section": "Compte",
    "nav.auth.session": "Mon compte",
    "nav.auth.users": "Utilisateurs",

    "auth.session.title": "Mon compte",
    "auth.session.subtitle": "Session, identité et droits effectifs",
    "auth.session.identity": "Identité",
    "auth.session.field.name": "Nom complet",
    "auth.session.field.email": "Adresse e-mail",
    "auth.session.field.user": "Identifiant utilisateur",
    "auth.session.field.lab": "Laboratoire",
    "auth.session.field.owner": "Propriétaire",
    "auth.session.field.owner.yes": "Propriétaire du laboratoire",
    "auth.session.field.owner.no": "Utilisateur",
    "auth.session.field.presets": "Profils (presets)",
    "auth.session.none": "—",

    "auth.session.tokens": "Session active",
    "auth.session.token.access": "Jeton d'accès",
    "auth.session.token.expires": "Expiration du jeton",
    "auth.session.token.valid": "Valide",
    "auth.session.token.expiring": "Expiration imminente",
    "auth.session.token.absent": "Aucun jeton",
    "auth.session.token.refresh": "Renouveler le jeton",
    "auth.session.token.refreshed": "Jeton renouvelé.",
    "auth.session.token.refresh.error": "Échec du renouvellement du jeton.",

    "auth.session.caps.title": "Droits effectifs",
    "auth.session.caps.count": "{n} capacité(s) effective(s)",
    "auth.session.caps.empty.title": "Aucune capacité",
    "auth.session.caps.empty.desc":
      "Votre session ne porte aucun droit. La surface d'identité du serveur n'est peut-être pas encore disponible (refus par défaut).",
    "auth.session.caps.refresh": "Rafraîchir les droits",
    "auth.session.caps.refreshed": "Droits rafraîchis.",
    "auth.session.caps.refresh.error": "Échec du rafraîchissement des droits.",
    "auth.session.caps.col": "Capacité",

    "auth.session.kill.title": "Terminer la session",
    "auth.session.kill.desc":
      "Vous serez déconnecté de ce poste et la famille de jetons de rafraîchissement sera révoquée côté serveur.",
    "auth.session.kill.action": "Se déconnecter",
    "auth.session.kill.confirm.title": "Confirmer la déconnexion",
    "auth.session.kill.confirm.body":
      "Confirmez-vous la fin de session ? Toute saisie non enregistrée sera perdue.",
    "auth.session.kill.confirm.ok": "Terminer la session",
    "auth.session.kill.cancel": "Annuler",
    "auth.session.kill.done": "Session terminée.",
    "auth.session.kill.error": "La déconnexion a échoué — réessayez.",

    "auth.session.expired.title": "Session expirée",
    "auth.session.expired.desc": "Votre session n'est plus active. Reconnectez-vous pour continuer.",
    "auth.session.reauth": "Se reconnecter",

    "auth.login.title": "Connexion",
    "auth.login.email": "Adresse e-mail",
    "auth.login.password": "Mot de passe",
    "auth.login.submit": "Se connecter",
    "auth.login.submitting": "Connexion…",
    "auth.login.error": "Identifiants invalides.",
    "auth.login.success": "Connexion réussie.",
    "auth.login.email.required": "Saisissez votre adresse e-mail.",
    "auth.login.password.required": "Saisissez votre mot de passe.",

    "auth.reset.title": "Mot de passe oublié",
    "auth.reset.desc":
      "Saisissez votre adresse e-mail ; si un compte existe, un lien de réinitialisation sera envoyé.",
    "auth.reset.submit": "Envoyer le lien",
    "auth.reset.submitting": "Envoi…",
    "auth.reset.done": "Si un compte correspond, un e-mail a été envoyé.",
    "auth.reset.error": "Demande impossible — réessayez plus tard.",
    "auth.reset.open": "Mot de passe oublié ?",

    "auth.users.title": "Gestion des utilisateurs",
    "auth.users.subtitle": "Comptes, profils et droits du laboratoire",
    "auth.users.empty.title": "Surface non disponible",
    "auth.users.empty.desc":
      "La gestion des utilisateurs n'est pas encore exposée par le serveur (module iam à venir).",
    "auth.users.col.name": "Nom",
    "auth.users.col.email": "E-mail",
    "auth.users.col.role": "Profil",
  });

  registerMessages("ar", {
    "nav.auth.section": "الحساب",
    "nav.auth.session": "حسابي",
    "nav.auth.users": "المستخدمون",

    "auth.session.title": "حسابي",
    "auth.session.subtitle": "الجلسة والهوية والصلاحيات الفعلية",
    "auth.session.identity": "الهوية",
    "auth.session.field.name": "الاسم الكامل",
    "auth.session.field.email": "البريد الإلكتروني",
    "auth.session.field.user": "معرّف المستخدم",
    "auth.session.field.lab": "المختبر",
    "auth.session.field.owner": "المالك",
    "auth.session.field.owner.yes": "مالك المختبر",
    "auth.session.field.owner.no": "مستخدم",
    "auth.session.field.presets": "الملفات (presets)",
    "auth.session.none": "—",

    "auth.session.tokens": "الجلسة النشطة",
    "auth.session.token.access": "رمز الوصول",
    "auth.session.token.expires": "انتهاء صلاحية الرمز",
    "auth.session.token.valid": "صالح",
    "auth.session.token.expiring": "انتهاء وشيك",
    "auth.session.token.absent": "لا يوجد رمز",
    "auth.session.token.refresh": "تجديد الرمز",
    "auth.session.token.refreshed": "تم تجديد الرمز.",
    "auth.session.token.refresh.error": "فشل تجديد الرمز.",

    "auth.session.caps.title": "الصلاحيات الفعلية",
    "auth.session.caps.count": "{n} صلاحية فعلية",
    "auth.session.caps.empty.title": "لا توجد صلاحيات",
    "auth.session.caps.empty.desc":
      "لا تحمل جلستك أي صلاحية. قد لا تكون واجهة الهوية في الخادم متاحة بعد (الرفض الافتراضي).",
    "auth.session.caps.refresh": "تحديث الصلاحيات",
    "auth.session.caps.refreshed": "تم تحديث الصلاحيات.",
    "auth.session.caps.refresh.error": "فشل تحديث الصلاحيات.",
    "auth.session.caps.col": "الصلاحية",

    "auth.session.kill.title": "إنهاء الجلسة",
    "auth.session.kill.desc": "سيتم تسجيل خروجك من هذا الجهاز وإبطال رموز التجديد في الخادم.",
    "auth.session.kill.action": "تسجيل الخروج",
    "auth.session.kill.confirm.title": "تأكيد تسجيل الخروج",
    "auth.session.kill.confirm.body": "هل تؤكد إنهاء الجلسة؟ ستفقد أي إدخال غير محفوظ.",
    "auth.session.kill.confirm.ok": "إنهاء الجلسة",
    "auth.session.kill.cancel": "إلغاء",
    "auth.session.kill.done": "تم إنهاء الجلسة.",
    "auth.session.kill.error": "فشل تسجيل الخروج — أعد المحاولة.",

    "auth.session.expired.title": "انتهت الجلسة",
    "auth.session.expired.desc": "لم تعد جلستك نشطة. أعد تسجيل الدخول للمتابعة.",
    "auth.session.reauth": "إعادة تسجيل الدخول",

    "auth.login.title": "تسجيل الدخول",
    "auth.login.email": "البريد الإلكتروني",
    "auth.login.password": "كلمة المرور",
    "auth.login.submit": "دخول",
    "auth.login.submitting": "جارٍ تسجيل الدخول…",
    "auth.login.error": "بيانات الاعتماد غير صحيحة.",
    "auth.login.success": "تم تسجيل الدخول.",
    "auth.login.email.required": "أدخل بريدك الإلكتروني.",
    "auth.login.password.required": "أدخل كلمة المرور.",

    "auth.reset.title": "نسيت كلمة المرور",
    "auth.reset.desc": "أدخل بريدك الإلكتروني؛ إذا كان هناك حساب، فسيتم إرسال رابط لإعادة التعيين.",
    "auth.reset.submit": "إرسال الرابط",
    "auth.reset.submitting": "جارٍ الإرسال…",
    "auth.reset.done": "إذا كان هناك حساب مطابق، فقد تم إرسال بريد إلكتروني.",
    "auth.reset.error": "تعذر تنفيذ الطلب — أعد المحاولة لاحقًا.",
    "auth.reset.open": "نسيت كلمة المرور؟",

    "auth.users.title": "إدارة المستخدمين",
    "auth.users.subtitle": "الحسابات والملفات وصلاحيات المختبر",
    "auth.users.empty.title": "الواجهة غير متاحة",
    "auth.users.empty.desc": "لم يكشف الخادم بعد عن إدارة المستخدمين (وحدة iam قادمة).",
    "auth.users.col.name": "الاسم",
    "auth.users.col.email": "البريد الإلكتروني",
    "auth.users.col.role": "الملف",
  });
}

/**
 * Capability strings consumed by this module — the server's exact keys (mirror of
 * server/src/modules/iam). The self/session surface is session-scoped (no capability); user
 * management is the one gated mutating surface.
 */
export const AUTH_CAPS = {
  /** Gates the "Utilisateurs" management surface (server: GET /utilisateurs). */
  usersManage: "iam.utilisateur.manage",
} as const;
